/**
 * VoiceSpendPanel — Owner-only at-a-glance roll-up of per-tenant
 * ElevenLabs voice-briefing character spend for the current month.
 *
 * Pairs with the iter136 email alert: lets the Owner spot a runaway
 * pilot *before* the 90% email even lands. Red dot = ≥90% (alert
 * either already fired this month or about to). Amber = 60–89%.
 *
 * Source: GET /api/owner/voice-spend/summary
 */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mic2, RefreshCw, RotateCcw, Loader2 } from "lucide-react";

const BAND_DOT = {
  red: "bg-red-500",
  amber: "bg-amber-400",
  green: "bg-emerald-500",
};

const BAND_LABEL = {
  red: "≥90%",
  amber: "60–89%",
  green: "<60%",
};

function fmt(n) {
  return new Intl.NumberFormat("en-GB").format(n || 0);
}

function RelTime({ iso }) {
  if (!iso) return <span className="text-ek-ink/40">—</span>;
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  } catch {
    return iso;
  }
}

export function VoiceSpendPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resettingId, setResettingId] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const r = await api.get("/owner/voice-spend/summary");
      setData(r.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const reset = async (row) => {
    const msg = row.used > 0
      ? `Reset ${row.organisation}'s voice-spend counter for this month? `
        + `Current usage (${new Intl.NumberFormat("en-GB").format(row.used)} chars) `
        + `will be cleared and the 90% / 100% alert flags reset. This is audit-logged.`
      : `Clear the alert flags on ${row.organisation}? (Counter is already at 0.)`;
    if (!window.confirm(msg)) return;
    setResettingId(row.tenant_id);
    try {
      await api.post(`/owner/voice-spend/${encodeURIComponent(row.tenant_id)}/reset`);
      toast.success(`Reset ${row.organisation}'s voice-spend counter.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Reset failed");
    } finally {
      setResettingId(null);
    }
  };

  return (
    <section
      className="mt-6 rounded-2xl border border-black/5 bg-white p-6"
      data-testid="voice-spend-panel"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-ek-purple/10">
            <Mic2 className="h-4 w-4 text-ek-purple" />
          </span>
          <div>
            <h2 className="font-display text-lg font-semibold text-ek-ink">
              Voice-briefing spend this month
            </h2>
            <p className="mt-1 text-sm text-ek-ink/70">
              Per-tenant ElevenLabs character usage against the configured budget.
              Red dot fires the same threshold as the 90% email alert.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          className="rounded-lg border border-black/10 px-3 py-1.5 text-xs font-semibold text-ek-ink hover:bg-black/5"
          data-testid="voice-spend-refresh"
        >
          <RefreshCw className="mr-1 inline h-3 w-3" /> Refresh
        </button>
      </div>

      {loading && (
        <p className="mt-4 text-sm text-ek-ink/60" data-testid="voice-spend-loading">
          Loading…
        </p>
      )}
      {err && !loading && (
        <p className="mt-4 text-sm text-red-600" data-testid="voice-spend-error">
          {err}
        </p>
      )}

      {!loading && !err && data && (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Month" value={data.month} testid="voice-spend-month" />
            <Stat
              label="Tenants tracked"
              value={fmt(data.totals.tenant_count)}
              testid="voice-spend-tenant-count"
            />
            <Stat
              label="At 60–89% (amber)"
              value={fmt(data.totals.amber_count)}
              dot="amber"
              testid="voice-spend-amber-count"
            />
            <Stat
              label="At ≥90% (red)"
              value={fmt(data.totals.red_count)}
              dot="red"
              testid="voice-spend-red-count"
            />
          </div>

          {data.items.length === 0 ? (
            <p className="mt-4 text-sm text-ek-ink/60" data-testid="voice-spend-empty">
              No tenants configured yet.
            </p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="voice-spend-table">
                <thead>
                  <tr className="border-b border-black/5 text-xs uppercase tracking-wide text-ek-ink/60">
                    <th className="py-2 pr-3 font-medium">Tenant</th>
                    <th className="py-2 pr-3 font-medium">Used / Budget</th>
                    <th className="py-2 pr-3 font-medium">%</th>
                    <th className="py-2 pr-3 font-medium">Auto-refresh</th>
                    <th className="py-2 pr-3 font-medium">Last synth</th>
                    <th className="py-2 pr-3 font-medium">Alert</th>
                    <th className="py-2 pr-3 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => (
                    <tr
                      key={row.tenant_id}
                      className="border-b border-black/5 last:border-0"
                      data-testid={`voice-spend-row-${row.tenant_id}`}
                    >
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          <span
                            className={`h-2 w-2 rounded-full ${BAND_DOT[row.band] || BAND_DOT.green}`}
                            aria-label={`band ${row.band} (${BAND_LABEL[row.band] || ""})`}
                            data-testid={`voice-spend-band-${row.tenant_id}`}
                          />
                          <span className="font-medium text-ek-ink">{row.organisation}</span>
                        </div>
                        <span className="text-xs text-ek-ink/50">{row.tenant_id}</span>
                      </td>
                      <td className="py-2 pr-3 tabular-nums text-ek-ink/80">
                        {fmt(row.used)} / {fmt(row.budget)}
                      </td>
                      <td
                        className="py-2 pr-3 tabular-nums font-semibold"
                        data-testid={`voice-spend-pct-${row.tenant_id}`}
                      >
                        {row.percent}%
                      </td>
                      <td className="py-2 pr-3 text-xs">
                        {row.autowarm ? (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">
                            ON
                          </span>
                        ) : (
                          <span className="rounded-full bg-black/5 px-2 py-0.5 text-ek-ink/60">
                            Manual
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-xs text-ek-ink/70">
                        <RelTime iso={row.last_synth_at} />
                      </td>
                      <td className="py-2 pr-3 text-xs">
                        {row.alert_100_sent ? (
                          <span className="rounded-full bg-red-50 px-2 py-0.5 text-red-700">
                            100% sent
                          </span>
                        ) : row.alert_90_sent ? (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">
                            90% sent
                          </span>
                        ) : (
                          <span className="text-ek-ink/40">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <button
                          type="button"
                          onClick={() => reset(row)}
                          disabled={resettingId === row.tenant_id}
                          className="inline-flex items-center gap-1 rounded-md border border-black/10 px-2 py-0.5 text-[11px] font-semibold text-ek-ink hover:border-ek-purple/40 hover:text-ek-purple disabled:opacity-60"
                          title="Zero the chars_used counter and clear 90% / 100% alert flags for the current month. Audit-logged."
                          data-testid={`voice-spend-reset-${row.tenant_id}`}
                        >
                          {resettingId === row.tenant_id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <RotateCcw className="h-3 w-3" />
                          )}
                          Reset
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Stat({ label, value, dot, testid }) {
  return (
    <div className="rounded-xl border border-black/5 bg-black/[0.02] px-3 py-2">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-ek-ink/55">
        {dot && (
          <span className={`h-1.5 w-1.5 rounded-full ${BAND_DOT[dot]}`} aria-hidden="true" />
        )}
        {label}
      </div>
      <div className="mt-0.5 text-base font-semibold text-ek-ink" data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

export default VoiceSpendPanel;
